/**
 * Super Admin API for subscription management.
 * Handles subscription plans, company subscriptions, billing contacts and limits.
 */

import { isSupabaseConfigured, supabase } from '../../domains/auth/supabaseClient';

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  price_monthly: number;
  price_annual: number;
  max_companies: number | null;
  max_users_per_company: number | null;
  features: string[];
  currency: string;
  stripe_product_id: string | null;
  stripe_price_monthly_id: string | null;
  stripe_price_annual_id: string | null;
  is_active: boolean;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

export interface CompanySubscription {
  id: string;
  company_id: string;
  plan_id: string;
  plan?: SubscriptionPlan;
  start_date: string;
  end_date: string | null;
  status: 'active' | 'trial' | 'grace' | 'cancelled' | 'expired' | 'suspended';
  payment_method: string | null;
  billing_cycle: 'monthly' | 'annual';
  cancel_at_period_end: boolean;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateSubscriptionInput {
  company_id: string;
  plan_id: string;
  end_date?: string | null;
  status?: 'active' | 'trial' | 'grace';
  payment_method?: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
}

export interface UpdateSubscriptionInput {
  subscription_id: string;
  plan_id?: string;
  status?: 'active' | 'trial' | 'grace' | 'cancelled' | 'expired' | 'suspended';
  end_date?: string | null;
  payment_method?: string;
}

export interface CreatePlanInput {
  name: string;
  description?: string;
  price_monthly: number;
  price_annual: number;
  max_companies?: number | null;
  max_users_per_company?: number | null;
  features?: string[];
  currency?: string;
  is_published?: boolean;
}

export interface PlatformBillingContact {
  platform_name: string;
  billing_email: string;
  billing_telegram_url: string | null;
  billing_whatsapp_url: string | null;
  billing_portal_url: string | null;
  billing_contact_note: string | null;
  grace_period_days: number;
  renewal_notice_days: number[];
}

const DEFAULT_BILLING_CONTACT: PlatformBillingContact = {
  platform_name: 'Кворум',
  billing_email: 'FieldSnap@ya.ru',
  billing_telegram_url: 'https://t.me/fieldsnap_support',
  billing_whatsapp_url: null,
  billing_portal_url: null,
  billing_contact_note: 'Для продления, смены тарифа и подтверждения оплаты свяжитесь с поддержкой.',
  grace_period_days: 3,
  renewal_notice_days: [7, 3, 1],
};

export async function fetchSubscriptionPlans(): Promise<SubscriptionPlan[]> {
  if (!isSupabaseConfigured) return [];

  const { data, error } = await supabase
    .from('subscription_plans')
    .select('*')
    .eq('is_active', true)
    .order('price_monthly', { ascending: true });

  if (error) throw new Error(`Ошибка загрузки планов: ${error.message}`);
  return (data ?? []).map((row: Record<string, unknown>) => ({
    ...row,
    // get_available_plans RPC возвращает max_users, а не max_users_per_company
    max_users_per_company: row.max_users_per_company ?? row.max_users ?? null,
  })) as SubscriptionPlan[];
}

export async function fetchAllSubscriptionPlans(): Promise<SubscriptionPlan[]> {
  if (!isSupabaseConfigured) return [];

  const { data, error } = await supabase
    .from('subscription_plans')
    .select('*')
    .order('price_monthly', { ascending: true });

  if (error) throw new Error(`Ошибка загрузки планов: ${error.message}`);
  return (data ?? []) as SubscriptionPlan[];
}

export async function createSubscriptionPlan(input: CreatePlanInput): Promise<SubscriptionPlan> {
  if (!isSupabaseConfigured) {
    throw new Error('Supabase не настроен');
  }

  const { data, error } = await supabase.functions.invoke('create-subscription-plan', {
    body: input,
  });

  if (error) {
    throw new Error(error.message ?? 'Ошибка создания плана');
  }

  return data as SubscriptionPlan;
}

/** @deprecated ЮKassa не требует синхронизации каталога. Функция-заглушка. */
export async function syncStripeCatalog(_planId: string): Promise<SubscriptionPlan> {
  throw new Error(
    'Sync каталога не нужен при использовании ЮKassa. ' +
    'Тариф активируется напрямую через update-subscription.',
  );
}

export async function updateSubscriptionPlan(
  planId: string,
  input: Partial<CreatePlanInput>,
): Promise<SubscriptionPlan> {
  if (!isSupabaseConfigured) {
    throw new Error('Supabase не настроен');
  }

  const { data, error } = await supabase
    .from('subscription_plans')
    .update(input)
    .eq('id', planId)
    .select()
    .single();

  if (error) {
    throw new Error(`Ошибка обновления плана: ${error.message}`);
  }

  return data as SubscriptionPlan;
}

export async function deleteSubscriptionPlan(planId: string): Promise<void> {
  if (!isSupabaseConfigured) {
    throw new Error('Supabase не настроен');
  }

  const { error } = await supabase
    .from('subscription_plans')
    .update({ is_active: false })
    .eq('id', planId);

  if (error) {
    throw new Error(`Ошибка удаления плана: ${error.message}`);
  }
}

export async function fetchCompanySubscription(companyId: string): Promise<CompanySubscription | null> {
  if (!isSupabaseConfigured) return null;

  const { data, error } = await supabase
    .from('company_subscriptions')
    .select(`
      id,
      company_id,
      plan_id,
      start_date,
      end_date,
      status,
      payment_method,
      billing_cycle,
      cancel_at_period_end,
      stripe_customer_id,
      stripe_subscription_id,
      created_at,
      updated_at,
      subscription_plans:plan_id (*)
    `)
    .eq('company_id', companyId)
    .in('status', ['active', 'trial', 'grace', 'cancelled', 'expired', 'suspended'])
    .order('updated_at', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw new Error(`Ошибка загрузки подписки: ${error.message}`);
  if (!data) return null;

  return {
    id: data.id,
    company_id: data.company_id,
    plan_id: data.plan_id,
    plan: data.subscription_plans as SubscriptionPlan,
    start_date: data.start_date,
    end_date: data.end_date,
    status: data.status,
    payment_method: data.payment_method,
    billing_cycle: (data.billing_cycle ?? 'monthly') as 'monthly' | 'annual',
    cancel_at_period_end: Boolean(data.cancel_at_period_end),
    stripe_customer_id: data.stripe_customer_id,
    stripe_subscription_id: data.stripe_subscription_id,
    created_at: data.created_at,
    updated_at: data.updated_at,
  } as CompanySubscription;
}

export async function createSubscription(input: CreateSubscriptionInput): Promise<CompanySubscription> {
  if (!isSupabaseConfigured) {
    throw new Error('Supabase не настроен');
  }

  const { data, error } = await supabase.functions.invoke('create-subscription', {
    body: input,
  });

  if (error) {
    throw new Error(error.message ?? 'Ошибка создания подписки');
  }

  return data.subscription as CompanySubscription;
}

export async function updateSubscription(input: UpdateSubscriptionInput): Promise<CompanySubscription> {
  if (!isSupabaseConfigured) {
    throw new Error('Supabase не настроен');
  }

  const { data, error } = await supabase.functions.invoke('update-subscription', {
    body: input,
  });

  if (error) {
    throw new Error(error.message ?? 'Ошибка обновления подписки');
  }

  return data.subscription as CompanySubscription;
}

export async function getCompanyLimits(companyId: string): Promise<{
  max_users: number | null;
  max_companies: number | null;
  features: string[];
  subscription_status: string;
  subscription_end_date: string | null;
  plan_name?: string | null;
} | null> {
  if (!isSupabaseConfigured) return null;

  // Параметр функции называется p_company_id (переименован в миграции 20260513000001)
  const { data, error } = await supabase.rpc('get_company_limits', {
    p_company_id: companyId,
  });

  if (error) {
    // 1.19.2 — Раньше тут было `return null`, но это маскировало сетевые
    // ошибки (offline, 5xx, таймаут) под легитимный ответ "у компании нет
    // подписки". В результате useSubscription уходил в status='none' /
    // features=[] и:
    //  • Signature.tsx пропускал экран подписи (hasFeature вернул false).
    //  • SubscriptionNudgeController срабатывал офлайн.
    // Теперь бросаем исключение; useSubscription поймает его, переведёт
    // в setError() и сохранит последнее успешное состояние из кэша.
    console.warn(`Ошибка получения лимитов: ${error.message}`);
    throw new Error(error.message ?? 'subscription_fetch_failed');
  }

  if (!data || data.length === 0) return null;

  const row = data[0];

  // RPC возвращает features как jsonb (массив строк в БД).
  // Supabase JS десериализует jsonb → уже JS-массив, но на всякий случай
  // нормализуем: принимаем string[], уже разобранный массив или JSON-строку.
  let features: string[] = [];
  if (Array.isArray(row.features)) {
    features = row.features as string[];
  } else if (typeof row.features === 'string') {
    try {
      const parsed = JSON.parse(row.features);
      features = Array.isArray(parsed) ? parsed : [];
    } catch {
      features = [];
    }
  }

  return { ...row, features };
}

export async function getAvailablePlans(): Promise<SubscriptionPlan[]> {
  if (!isSupabaseConfigured) return [];

  const { data, error } = await supabase.rpc('get_available_plans');

  if (error) {
    console.warn(`Ошибка получения доступных планов: ${error.message}`);
    return [];
  }

  return (data ?? []).map((row: Record<string, unknown>) => ({
    ...row,
    max_users_per_company: row.max_users_per_company ?? row.max_users ?? null,
  })) as SubscriptionPlan[];
}

/**
 * Создаёт платёж ЮKassa и возвращает URL для перехода пользователя на форму оплаты.
 *
 * 1.26.0 — P0-2: idempotency_key + правильная обработка non-2xx.
 *   • Клиент генерирует UUID v4 один раз при открытии формы оплаты (useRef)
 *     и передаёт сюда. См. SubscriptionCenter, PlanChangeDialog, Billing.tsx.
 *   • Тот же ключ при повторном вызове → реюз существующего confirmation_url.
 *   • Защита от двойного клика, network retry, перезагрузки страницы оплаты.
 *
 * 1.26.0 hotfix — Supabase JS SDK на non-2xx (включая 503 stub) кидает
 *   FunctionsHttpError, и тело ответа в `data` НЕ попадает. Чтобы UI
 *   показывал понятное сообщение от сервера (а не общее "non-2xx"),
 *   разворачиваем `error.context` (это Response object) и читаем JSON-тело
 *   вручную. Это работает с supabase-js v2.30+.
 */
export async function createPayment(input: {
  company_id:       string;
  plan_id:          string;
  billing_cycle:    'monthly' | 'annual';
  return_url?:      string;
  idempotency_key?: string;
}): Promise<{ confirmation_url: string; payment_id: string; invoice_id: string }> {
  if (!isSupabaseConfigured) {
    throw new Error('Supabase не настроен');
  }

  const { data, error } = await supabase.functions.invoke('create-payment', {
    body: input,
  });

  // ─── Non-2xx ответ — SDK завернул в error, тело надо прочитать вручную ──
  if (error) {
    // supabase-js v2: error.context — это Response (или undefined в старых версиях).
    // Пробуем разобрать тело, чтобы достать stub/error message.
    let serverBody: Record<string, unknown> | null = null;
    const ctx = (error as unknown as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        serverBody = await ctx.clone().json();
      } catch {
        serverBody = null;
      }
    }

    // Stub-режим из 503: показываем дружелюбное сообщение
    if (serverBody && serverBody.stub === true) {
      const msg = (serverBody.error as string | undefined) ?? 'Платёжный провайдер ещё не подключён.';
      const email = serverBody.support_email ? ` Напишите на ${serverBody.support_email}` : '';
      throw new Error(`${msg}${email}`);
    }

    // Любая другая 4xx/5xx: берём message из тела, если он там есть
    const serverMessage = (serverBody?.error as string | undefined) ?? error.message;
    throw new Error(serverMessage ?? 'Ошибка создания платежа ЮKassa');
  }

  // На случай 2xx с полем stub (редкий, но возможный кейс)
  if (data && data.stub === true) {
    const msg = data.error ?? 'Платёжный провайдер ещё не подключён.';
    const email = data.support_email ? ` Напишите на ${data.support_email}` : '';
    throw new Error(`${msg}${email}`);
  }

  return data as { confirmation_url: string; payment_id: string; invoice_id: string };
}

/** @deprecated Используй createPayment() вместо этой функции. */
export async function createCheckoutSession(input: {
  company_id:    string;
  plan_id:       string;
  billing_cycle: 'monthly' | 'annual';
}): Promise<{ checkout_url: string; checkout_session_id: string }> {
  const result = await createPayment(input);
  // Обратная совместимость для старых вызовов
  return {
    checkout_url:        result.confirmation_url,
    checkout_session_id: result.payment_id,
  };
}

/**
 * @deprecated Stripe Customer Portal недоступен при ЮKassa.
 * Для смены карты создаётся новый платёж с save_payment_method=true.
 * Используй createPayment() с текущим тарифом.
 */
export async function createBillingPortalSession(
  _companyId: string,
): Promise<{ portal_url: string; portal_session_id: string }> {
  throw new Error(
    'Stripe Customer Portal не используется. ' +
    'Для обновления способа оплаты создайте новый платёж через ЮKassa.',
  );
}

// ─── Billing ledger (Pass 2) ─────────────────────────────────────────────────

export interface BillingInvoiceRow {
  id: string;
  company_id: string;
  company_name: string | null;
  subscription_id: string | null;
  plan_name: string | null;
  source: string;
  external_id: string | null;
  invoice_number: string | null;
  invoice_status: string;
  amount_due: number;
  amount_paid: number;
  currency: string;
  period_start: string | null;
  period_end: string | null;
  due_date: string | null;
  paid_at: string | null;
  hosted_invoice_url: string | null;
  invoice_pdf_url: string | null;
  // dunning state — populated by list_billing_invoices when invoice is in dunning flow
  dunning_attempt_count: number | null;
  dunning_max_retries: number | null;
  next_retry_at: string | null;
  dunning_last_error: string | null;
  dunning_finalized_at: string | null;
  created_at: string;
}

export interface BillingPaymentRow {
  id: string;
  company_id: string;
  company_name: string | null;
  subscription_id: string | null;
  invoice_id: string | null;
  source: string;
  external_id: string | null;
  payment_status: string;
  amount: number;
  currency: string;
  payment_method: string | null;
  paid_at: string | null;
  created_at: string;
}

export interface BillingEventRow {
  id: string;
  company_id: string;
  company_name: string | null;
  subscription_id: string | null;
  event_type: string;
  source: string;
  event_status: string;
  amount: number | null;
  currency: string | null;
  occurred_at: string;
  actor_id: string | null;
  actor_name: string | null;
  actor_email: string | null;
  stripe_event_id: string | null;
  external_reference: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
}

export interface BillingLedgerSummary {
  total_invoices: number;
  total_paid_amount: number;
  total_outstanding: number;
  open_invoices: number;
  failed_invoices: number;
  last_payment_at: string | null;
  last_failed_event_at: string | null;
}

export interface ReminderLogRow {
  id: string;
  subscription_id: string;
  company_id: string;
  company_name: string | null;
  reminder_day: number;
  reminder_status: 'pending' | 'sent' | 'failed' | 'skipped';
  channel: string;
  scheduled_for: string;
  sent_at: string | null;
  error_message: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export async function fetchBillingInvoices(
  options: { companyId?: string | null; limit?: number; offset?: number } = {},
): Promise<BillingInvoiceRow[]> {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase.rpc('list_billing_invoices', {
    p_company_id: options.companyId ?? null,
    p_limit: options.limit ?? 50,
    p_offset: options.offset ?? 0,
  });
  if (error) throw new Error(`Ошибка загрузки инвойсов: ${error.message}`);
  return (data ?? []) as BillingInvoiceRow[];
}

export async function fetchBillingPayments(
  options: { companyId?: string | null; limit?: number; offset?: number } = {},
): Promise<BillingPaymentRow[]> {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase.rpc('list_billing_payments', {
    p_company_id: options.companyId ?? null,
    p_limit: options.limit ?? 50,
    p_offset: options.offset ?? 0,
  });
  if (error) throw new Error(`Ошибка загрузки платежей: ${error.message}`);
  return (data ?? []) as BillingPaymentRow[];
}

export async function fetchBillingEvents(
  options: { companyId?: string | null; limit?: number; offset?: number } = {},
): Promise<BillingEventRow[]> {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase.rpc('list_billing_events', {
    p_company_id: options.companyId ?? null,
    p_limit: options.limit ?? 100,
    p_offset: options.offset ?? 0,
  });
  if (error) throw new Error(`Ошибка загрузки billing-событий: ${error.message}`);
  return (data ?? []) as BillingEventRow[];
}

export async function fetchBillingLedgerSummary(
  companyId?: string | null,
): Promise<BillingLedgerSummary | null> {
  if (!isSupabaseConfigured) return null;
  const { data, error } = await supabase.rpc('billing_ledger_summary', {
    p_company_id: companyId ?? null,
  });
  if (error) throw new Error(`Ошибка загрузки summary: ${error.message}`);
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  return {
    total_invoices: Number(row.total_invoices ?? 0),
    total_paid_amount: Number(row.total_paid_amount ?? 0),
    total_outstanding: Number(row.total_outstanding ?? 0),
    open_invoices: Number(row.open_invoices ?? 0),
    failed_invoices: Number(row.failed_invoices ?? 0),
    last_payment_at: row.last_payment_at ?? null,
    last_failed_event_at: row.last_failed_event_at ?? null,
  } as BillingLedgerSummary;
}

export async function fetchReminderLog(
  options: { companyId?: string | null; limit?: number } = {},
): Promise<ReminderLogRow[]> {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase.rpc('list_subscription_reminders', {
    p_company_id: options.companyId ?? null,
    p_limit: options.limit ?? 50,
  });
  if (error) throw new Error(`Ошибка загрузки журнала напоминаний: ${error.message}`);
  return (data ?? []) as ReminderLogRow[];
}

export async function fetchPlatformBillingContact(): Promise<PlatformBillingContact> {
  if (!isSupabaseConfigured) return DEFAULT_BILLING_CONTACT;

  const { data, error } = await supabase.rpc('get_platform_billing_contact');
  if (error) {
    console.warn(`Ошибка загрузки billing contacts: ${error.message}`);
    return DEFAULT_BILLING_CONTACT;
  }

  const row = Array.isArray(data) ? data[0] : null;
  if (!row) return DEFAULT_BILLING_CONTACT;

  return {
    platform_name: String(row.platform_name ?? DEFAULT_BILLING_CONTACT.platform_name),
    billing_email: String(row.billing_email ?? DEFAULT_BILLING_CONTACT.billing_email),
    billing_telegram_url: row.billing_telegram_url ? String(row.billing_telegram_url) : null,
    billing_whatsapp_url: row.billing_whatsapp_url ? String(row.billing_whatsapp_url) : null,
    billing_portal_url: row.billing_portal_url ? String(row.billing_portal_url) : null,
    billing_contact_note: row.billing_contact_note ? String(row.billing_contact_note) : null,
    grace_period_days: Number(row.grace_period_days ?? DEFAULT_BILLING_CONTACT.grace_period_days),
    renewal_notice_days: Array.isArray(row.renewal_notice_days)
      ? row.renewal_notice_days.map((value: unknown) => Number(value)).filter((value: number) => Number.isFinite(value))
      : DEFAULT_BILLING_CONTACT.renewal_notice_days,
  };
}

// ─── Payment methods ──────────────────────────────────────────────────────────

export interface CompanyPaymentMethod {
  id:         string;
  provider:   string;          // 'yookassa' | 'manual'
  brand:      string | null;   // 'visa' | 'mastercard' | 'mir' и т.д.
  last4:      string | null;
  exp_month:  number | null;
  exp_year:   number | null;
  is_default: boolean;
}

/**
 * Fetches active (attached) payment methods for a company.
 * Uses get_company_payment_methods RPC which enforces company membership check.
 */
export async function fetchCompanyPaymentMethods(companyId: string): Promise<CompanyPaymentMethod[]> {
  if (!isSupabaseConfigured) return [];

  const { data, error } = await supabase.rpc('get_company_payment_methods', {
    p_company_id: companyId,
  });

  if (error) {
    console.warn(`Ошибка загрузки payment methods: ${error.message}`);
    return [];
  }

  return (data ?? []) as CompanyPaymentMethod[];
}

